// Landing page theme presets. Each theme exposes CSS variables that the
// PublicLanding consumes (background, accents, surfaces, button styles).
// Used by:
//   - LandingEditor (theme picker + preview swatches)
//   - PublicLanding (renders root <div> with these CSS variables inline)

export const THEMES = {
  dark_lux: {
    key: "dark_lux",
    label: "Dark Lux",
    description: "Black & teal. Sleek, current default.",
    swatch: ["#000000", "#14B8A6", "#1f0a14"],
    vars: {
      "--lp-bg":        "radial-gradient(120% 80% at 50% 0%, #1f0a14 0%, #0a0410 45%, #000 100%)",
      "--lp-text":      "#fafafa",
      "--lp-muted":     "rgba(255,255,255,0.65)",
      "--lp-muted-2":   "rgba(255,255,255,0.40)",
      "--lp-surface":   "rgba(255,255,255,0.04)",
      "--lp-surface-2": "rgba(255,255,255,0.07)",
      "--lp-border":    "rgba(255,255,255,0.10)",
      "--lp-accent":    "#14B8A6",
      "--lp-accent-2":  "#0d9488",
      "--lp-cta-bg":    "linear-gradient(135deg,#f97316 0%,#ec4899 100%)",
      "--lp-cta-text":  "#000",
      "--lp-cta-shadow": "0 8px 28px -8px rgba(249,115,22,0.55)",
      "--lp-grain":     "0.06",
    },
  },
  neon_sin: {
    key: "neon_sin",
    label: "Neon Sin",
    description: "Deep purple-blue with magenta + cyan glow.",
    swatch: ["#0a0a23", "#ff2d95", "#22d3ee"],
    vars: {
      "--lp-bg":        "radial-gradient(120% 80% at 50% 0%, #1e1b4b 0%, #0a0a23 45%, #000 100%)",
      "--lp-text":      "#f5e8ff",
      "--lp-muted":     "rgba(245,232,255,0.70)",
      "--lp-muted-2":   "rgba(245,232,255,0.45)",
      "--lp-surface":   "rgba(168,85,247,0.10)",
      "--lp-surface-2": "rgba(168,85,247,0.15)",
      "--lp-border":    "rgba(255,45,149,0.30)",
      "--lp-accent":    "#ff2d95",
      "--lp-accent-2":  "#22d3ee",
      "--lp-cta-bg":    "linear-gradient(135deg,#ff2d95 0%,#22d3ee 100%)",
      "--lp-cta-text":  "#0a0a23",
      "--lp-cta-shadow": "0 0 40px -4px rgba(255,45,149,0.65), 0 0 80px -10px rgba(34,211,238,0.40)",
      "--lp-grain":     "0.04",
    },
  },
  bordello: {
    key: "bordello",
    label: "Bordello",
    description: "Wine red velvet with gold accents.",
    swatch: ["#2a0510", "#dc143c", "#d4af37"],
    vars: {
      "--lp-bg":        "radial-gradient(120% 80% at 50% 0%, #4a0a18 0%, #2a0510 50%, #0e0204 100%)",
      "--lp-text":      "#fff5ee",
      "--lp-muted":     "rgba(255,245,238,0.70)",
      "--lp-muted-2":   "rgba(212,175,55,0.55)",
      "--lp-surface":   "rgba(220,20,60,0.08)",
      "--lp-surface-2": "rgba(212,175,55,0.10)",
      "--lp-border":    "rgba(212,175,55,0.30)",
      "--lp-accent":    "#d4af37",
      "--lp-accent-2":  "#dc143c",
      "--lp-cta-bg":    "linear-gradient(135deg,#dc143c 0%,#d4af37 100%)",
      "--lp-cta-text":  "#1a0008",
      "--lp-cta-shadow": "0 8px 32px -8px rgba(220,20,60,0.60)",
      "--lp-grain":     "0.08",
    },
  },
  soft_sensual: {
    key: "soft_sensual",
    label: "Soft Sensual",
    description: "Pastel rose & cream. Warmer, gentler vibe.",
    swatch: ["#fff1f2", "#f43f5e", "#fb923c"],
    vars: {
      "--lp-bg":        "radial-gradient(120% 80% at 50% 0%, #ffe4e6 0%, #fff1f2 45%, #fef3c7 100%)",
      "--lp-text":      "#3f1c2a",
      "--lp-muted":     "rgba(63,28,42,0.70)",
      "--lp-muted-2":   "rgba(63,28,42,0.50)",
      "--lp-surface":   "rgba(255,255,255,0.55)",
      "--lp-surface-2": "rgba(255,255,255,0.75)",
      "--lp-border":    "rgba(244,63,94,0.25)",
      "--lp-accent":    "#f43f5e",
      "--lp-accent-2":  "#fb923c",
      "--lp-cta-bg":    "linear-gradient(135deg,#f43f5e 0%,#fb923c 100%)",
      "--lp-cta-text":  "#fff",
      "--lp-cta-shadow": "0 8px 28px -8px rgba(244,63,94,0.55)",
      "--lp-grain":     "0.03",
    },
  },
  greek_cyber: {
    key: "greek_cyber",
    label: "Greek Cyber",
    description: "Black + cyan glitch. Mediterranean tech.",
    swatch: ["#000000", "#00ffe0", "#0066ff"],
    vars: {
      "--lp-bg":        "linear-gradient(180deg,#000 0%,#001a1a 50%,#000 100%)",
      "--lp-text":      "#e0fffa",
      "--lp-muted":     "rgba(0,255,224,0.75)",
      "--lp-muted-2":   "rgba(0,255,224,0.50)",
      "--lp-surface":   "rgba(0,255,224,0.06)",
      "--lp-surface-2": "rgba(0,255,224,0.10)",
      "--lp-border":    "rgba(0,255,224,0.30)",
      "--lp-accent":    "#00ffe0",
      "--lp-accent-2":  "#0066ff",
      "--lp-cta-bg":    "linear-gradient(135deg,#00ffe0 0%,#0066ff 100%)",
      "--lp-cta-text":  "#000",
      "--lp-cta-shadow": "0 0 30px -4px rgba(0,255,224,0.70)",
      "--lp-grain":     "0.05",
    },
  },
};

export const THEME_KEYS = Object.keys(THEMES);
export const DEFAULT_THEME = "dark_lux";

export function themeVars(themeKey) {
  return (THEMES[themeKey] || THEMES[DEFAULT_THEME]).vars;
}
